import type { Diagnostic } from "./validateFlow";
import type { FlowDocument, FlowEdge } from "./types";

/**
 * The other structural port rule Go enforces (graph.go's `BuildGraph`, keyed
 * on `source + "\x00" + port`): two edges leaving the same node on the same
 * port can never both be real graph edges — which one "wins" would be
 * nondeterministic — so Go reports every edge after the first on a given
 * (source, port) pair as `CodeDuplicatePort`/`duplicate_port_edge` and
 * excludes it from the rest of the graph.
 *
 * Like `checkUnknownPorts` (requiredPorts.ts), this is a DIAGNOSTIC rather
 * than a guard inside `addEdge`: the canvas always creates a new edge with
 * the same default port name, so a hard rejection here would make it
 * impossible to draw a second edge from a node that already has one "next"
 * edge, before the Inspector gets a chance to rename either.
 */
export function checkDuplicatePort(document: FlowDocument): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const firstEdgeForPort = new Map<string, string>();
  for (const edge of document.edges) {
    const key = `${edge.source}\x00${edge.port}`;
    const prior = firstEdgeForPort.get(key);
    if (prior === undefined) {
      firstEdgeForPort.set(key, edge.id);
      continue;
    }
    diagnostics.push({
      layer: "semantic",
      code: "duplicate_port_edge",
      path: `/edges/${edge.id}`,
      detail: `port "${edge.port}" of node "${edge.source}" already has edge "${prior}"; port selection must be deterministic`,
    });
  }
  return diagnostics;
}

/**
 * Outgoing edges, deduplicated the same way Go's `BuildGraph` populates
 * `g.Out` — first edge wins per (source, port) pair. The cycle search below
 * must walk this, not `document.edges` directly: a duplicate-port edge is
 * already reported on its own (`checkDuplicatePort` above) and Go's own
 * graph never routes through it, so counting it again here could flag an
 * `unbounded_loop` the server would not agree exists — exactly the kind of
 * false rejection this slice is explicitly trying to avoid.
 */
function dedupedOutEdges(document: FlowDocument): Map<string, FlowEdge[]> {
  const byNode = new Map<string, FlowEdge[]>();
  const seen = new Set<string>();
  for (const edge of document.edges) {
    const key = `${edge.source}\x00${edge.port}`;
    if (seen.has(key)) continue;
    seen.add(key);
    const list = byNode.get(edge.source);
    if (list) {
      list.push(edge);
    } else {
      byNode.set(edge.source, [edge]);
    }
  }
  return byNode;
}

/**
 * Classic white/grey/black DFS cycle search, matching Go's `findCycle`
 * (semantic.go) node for node: a grey (in-progress) revisit is a cycle, a
 * black (finished) node is skipped, and the path is sliced from the
 * revisited node onward so the diagnostic can name exactly the nodes
 * involved, same as Go's own message.
 */
function findCycle(nodeIds: readonly string[], successors: (id: string) => string[]): string[] | null {
  const WHITE = 0,
    GREY = 1,
    BLACK = 2;
  const color = new Map<string, 0 | 1 | 2>();
  const path: string[] = [];
  let found: string[] | null = null;

  function visit(id: string): boolean {
    color.set(id, GREY);
    path.push(id);
    for (const next of successors(id)) {
      const state = color.get(next) ?? WHITE;
      if (state === GREY) {
        const start = path.indexOf(next);
        found = [...path.slice(start), next];
        return true;
      }
      if (state === WHITE && visit(next)) {
        return true;
      }
    }
    path.pop();
    color.set(id, BLACK);
    return false;
  }

  for (const id of nodeIds) {
    if ((color.get(id) ?? WHITE) === WHITE && visit(id)) {
      return found;
    }
  }
  return null;
}

/**
 * Mirrors Go's `checkLoops`/`CodeUnboundedLoop`: every cycle must pass
 * through a Repeat node, the only node type that counts visits and
 * eventually exits, so a Repeat's own successors are cut (treated as having
 * none) before searching — exactly Go's own comment on why this proves an
 * unbounded loop. Deliberately does NOT add a separate self-loop check: Go
 * has none either (a direct A->A edge is only ever caught here, as a
 * one-node cycle, same as any other cycle), and a self-loop is structurally
 * legal through a Repeat node on purpose.
 */
export function checkUnboundedLoop(document: FlowDocument): Diagnostic[] {
  const typeById = new Map(document.nodes.map((node) => [node.id, node.type]));
  const out = dedupedOutEdges(document);
  const successors = (id: string): string[] => {
    if (typeById.get(id) === "repeat") return [];
    const edges = out.get(id);
    if (!edges) return [];
    return [...edges.map((edge) => edge.target)].sort();
  };

  const cycle = findCycle(
    document.nodes.map((node) => node.id),
    successors,
  );
  if (!cycle) return [];

  return [
    {
      layer: "semantic",
      code: "unbounded_loop",
      path: `/nodes/${cycle[0]}`,
      detail: `cycle ${cycle.join(" -> ")} contains no repeat controller, so it has no visit counter and could run forever`,
    },
  ];
}
